import React, { useState } from 'react';
import { adminAPI } from '../services/api';

interface Order {
  _id: string;
  user: { name: string; phone: string };
  items: Array<{ name: string; quantity: number; price: number }>;
  totalAmount: number;
  status: string;
  deliveryAddress: string;
  assignedPartner?: { name: string };
  createdAt: string;
}

interface OrdersManagementProps {
  orders: Order[];
  onRefresh: () => void;
}

const OrdersManagement: React.FC<OrdersManagementProps> = ({ orders, onRefresh }) => {
  const [filter, setFilter] = useState<string>('All');

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      await adminAPI.updateOrderStatus(id, newStatus);
      alert('Order status updated');
      onRefresh();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await adminAPI.deleteOrder(id);
        alert('Order deleted successfully');
        onRefresh();
      } catch (error) {
        alert('Failed to delete order');
      }
    }
  };

  const filteredOrders = filter === 'All' 
    ? orders 
    : orders.filter(order => order.status === filter);

  const getStatusClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'Pending': 'status-pending',
      'Confirmed': 'status-confirmed',
      'Preparing': 'status-preparing',
      'Out for Delivery': 'status-delivering',
      'Delivered': 'status-delivered',
      'Cancelled': 'status-cancelled',
    };
    return statusMap[status] || 'status-pending';
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Orders Management</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          {['All', 'Pending', 'Confirmed', 'Preparing', 'Out for Delivery', 'Delivered', 'Cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '10px 20px',
                background: filter === status ? '#fc8019' : '#f5f5f5',
                color: filter === status ? 'white' : '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: filter === status ? 'bold' : 'normal',
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="empty-state">
          <h3>No Orders Found</h3>
          <p>No orders match the selected filter.</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Tip</th>
                <th>Status</th>
                <th>Partner</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order._id}>
                  <td>{order._id.slice(-6)}</td>
                  <td>
                    {order.user?.name || 'N/A'}<br/>
                    <small>{order.user?.phone || ''}</small>
                  </td>
                  <td>
                    {order.items?.slice(0, 2).map((item, idx) => (
                      <div key={idx}>{item.name} x{item.quantity}</div>
                    ))}
                    {order.items?.length > 2 && <small>+{order.items.length - 2} more</small>}
                  </td>
                  <td>₹{order.totalAmount?.toFixed(2) || '0.00'}</td>
                  <td style={{color: (order as any).tip > 0 ? '#28a745' : '#999', fontWeight: (order as any).tip > 0 ? 'bold' : 'normal'}}>
                    ₹{(order as any).tip || 0}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>{order.assignedPartner?.name || 'Unassigned'}</td>
                  <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td>
                    {order.status !== 'Delivered' && order.status !== 'Cancelled' && (
                      <select
                        onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                        value=""
                        style={{ padding: '5px', marginRight: '5px' }}
                      >
                        <option value="" disabled>Change Status</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Preparing">Preparing</option>
                        <option value="Out for Delivery">Out for Delivery</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    )}
                    <button className="action-btn delete-btn" onClick={() => handleDelete(order._id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrdersManagement;
